import { AnimatePresence, motion } from "framer-motion";
import React, { useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";

interface GalleryMenuProps {
  images: string[];
  businessName: string;
}

const GalleryMenu: React.FC<GalleryMenuProps> = ({ images, businessName }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);

  // Manual scroll - TIDAK AUTO
  const handleScroll = (direction: "left" | "right") => {
    const container = scrollRef.current;
    if (!container) return;

    const scrollAmount = direction === "right" ? 300 : -300;
    container.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const nextLightboxImage = () => {
    setLightboxIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevLightboxImage = () => {
    setLightboxIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  if (!images || images.length === 0) {
    return (
      <div className="w-full h-[400px] bg-slate-200 rounded-xl flex items-center justify-center">
        <span className="text-slate-500">Tidak ada menu tersedia</span>
      </div>
    );
  }

  return (
    <>
      <div className="relative w-full flex justify-center mt-6">
        <div
          className="relative rounded-lg"
          style={{
            width: "100%",
            maxWidth: "900px",
          }}
        >
          {/* Tombol geser kiri */}
          {images.length > 1 && (
            <button
              onClick={() => handleScroll("left")}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm p-2 md:p-2.5 rounded-full shadow-lg hover:bg-white hover:shadow-xl transition-all duration-200 flex items-center justify-center"
            >
              <FiChevronLeft className="h-4 w-4 md:h-5 md:w-5 text-slate-700" />
            </button>
          )}

          {/* Carousel container - FIXED, NO AUTO-SCROLL */}
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto px-10 py-2 scroll-smooth scrollbar-hide"
          >
            {images.map((img, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.03, y: -5 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => openLightbox(index)}
                className="flex-shrink-0 bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300"
                style={{
                  width: "250px",
                  height: "420px",
                }}
              >
                <div className="w-full h-[75%] relative group">
                  <img
                    src={img}
                    alt={`${businessName} - Menu ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {/* Overlay hint */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                    <div className="bg-white/90 px-3 py-1.5 rounded-full text-xs font-medium text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Klik untuk detail
                    </div>
                  </div>
                </div>
                <div className="p-3 text-center">
                  <h3 className="text-sm font-semibold text-slate-700">
                    Menu {index + 1}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {businessName}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Tombol geser kanan */}
          {images.length > 1 && (
            <button
              onClick={() => handleScroll("right")}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm p-2 md:p-2.5 rounded-full shadow-lg hover:bg-white hover:shadow-xl transition-all duration-200 flex items-center justify-center"
            >
              <FiChevronRight className="h-4 w-4 md:h-5 md:w-5 text-slate-700" />
            </button>
          )}
        </div>
      </div>

      {/* === Lightbox Modal === */}
      <AnimatePresence>
        {isLightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={closeLightbox}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors duration-300 z-10"
            >
              <FiX className="h-6 w-6 text-white" />
            </motion.button>

            {/* Image Container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative flex items-center justify-center w-full h-full px-20"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[lightboxIndex]}
                alt={`${businessName} - Menu Detail ${lightboxIndex + 1}`}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                style={{ width: 'auto', height: 'auto' }}
              />

              {/* Navigation Buttons */}
              {images.length > 1 && (
                <>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={prevLightboxImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur p-4 rounded-full transition-all duration-300"
                  >
                    <FiChevronLeft className="h-8 w-8 text-white" />
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={nextLightboxImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur p-4 rounded-full transition-all duration-300"
                  >
                    <FiChevronRight className="h-8 w-8 text-white" />
                  </motion.button>
                </>
              )}

              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-medium">
                Menu {lightboxIndex + 1} / {images.length}
              </div>

              {/* Business Name */}
              <div className="absolute top-4 left-4 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-medium">
                {businessName}
              </div>
            </motion.div>

            {/* Info Text */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white/70 text-sm">
              Tekan ESC atau klik di luar gambar untuk menutup
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hide scrollbar in CSS */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </>
  );
};

export default GalleryMenu;
